import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { downloadWidgetFile, listWidgetFiles, WidgetFileInfo } from '../services/storageService';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface HighlightRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface PDFHighlight {
  id: string;
  pageNumber: number;
  text: string;
  color: string;
  note: string;
  rects: HighlightRect[];
  createdAt: string;
}

const HIGHLIGHT_COLORS = [
  { name: 'Yellow', color: '#ffeb3b' },
  { name: 'Green', color: '#a5d6a7' },
  { name: 'Blue', color: '#90caf9' },
  { name: 'Pink', color: '#f48fb1' },
  { name: 'Orange', color: '#ffcc80' },
];

const HL_KEY = (id: string) => `file-viewer-hl:${id}`;
const NOTES_KEY = (id: string) => `file-viewer-notes:${id}`;
const load = <T,>(key: string, fallback: T): T => {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : fallback; } catch { return fallback; }
};
const save = (key: string, val: unknown) => {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* */ }
};

// Hard-coded dark colors so they work regardless of app theme
const S = {
  bg: '#111113',
  bgPanel: '#18181b',
  bgCard: '#1e1e22',
  bgHover: '#27272a',
  border: '#2e2e33',
  text: '#e4e4e7',
  textMuted: '#a1a1aa',
  textFaint: '#71717a',
};

const FileViewer: React.FC = () => {
  const { assignmentId, widgetId, fileId } = useParams<{ assignmentId: string; widgetId: string; fileId: string }>();

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [fileInfo, setFileInfo] = useState<WidgetFileInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageNotes, setImageNotes] = useState('');
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [highlightMode, setHighlightMode] = useState(false);
  const [showPanel, setShowPanel] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);

  const downloadFile = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileInfo?.fileName || 'download';
    a.click();
  };

  const copyViewerLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };
  const [highlightColor, setHighlightColor] = useState('#ffeb3b');
  const [highlights, setHighlights] = useState<PDFHighlight[]>([]);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const pdfAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!assignmentId || !widgetId || !fileId) return;
    const doLoad = async () => {
      setLoading(true);
      try {
        const [url, files] = await Promise.all([
          downloadWidgetFile(assignmentId, widgetId, fileId),
          listWidgetFiles(assignmentId, widgetId).catch(() => [] as WidgetFileInfo[]),
        ]);
        setBlobUrl(url);
        const info = files.find(f => f.id === fileId);
        if (info) setFileInfo(info);
      } catch { setError('Failed to load file.'); } finally { setLoading(false); }
    };
    doLoad();
    if (fileId) {
      setHighlights(load(HL_KEY(fileId), []));
      setImageNotes(load(NOTES_KEY(fileId), ''));
    }
  }, [assignmentId, widgetId, fileId]);

  const handleImageNotesChange = (v: string) => { setImageNotes(v); if (fileId) save(NOTES_KEY(fileId), v); };

  const updateHighlights = useCallback((updated: PDFHighlight[]) => {
    setHighlights(updated);
    if (fileId) save(HL_KEY(fileId), updated);
  }, [fileId]);

  const handleTextSelection = useCallback(() => {
    if (!highlightMode || !fileId || !pageContainerRef.current) return;
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.toString().trim()) return;

    // Find which page the selection is in
    const range = sel.getRangeAt(0);
    const startNode = range.startContainer;
    const pageEl = (startNode instanceof Element ? startNode : startNode.parentElement)?.closest('.react-pdf__Page');
    const pageNum = pageEl ? parseInt(pageEl.getAttribute('data-page-number') || '1', 10) : 1;

    // Get the page wrapper div (the relative container with highlights)
    const pageWrapper = pageEl?.parentElement;
    if (!pageWrapper) return;
    const pageRect = pageWrapper.getBoundingClientRect();
    const pw = pageRect.width;
    const ph = pageRect.height;
    if (pw === 0 || ph === 0) return;

    // Store as ratios relative to that page
    const rects: HighlightRect[] = Array.from(range.getClientRects())
      .map(rect => ({
        x: (rect.x - pageRect.x) / pw,
        y: (rect.y - pageRect.y) / ph,
        width: rect.width / pw,
        height: rect.height / ph,
      }))
      .filter(r => r.width > 0 && r.height > 0);

    const hl: PDFHighlight = {
      id: `hl_${Date.now()}`,
      pageNumber: pageNum,
      text: sel.toString().trim(),
      color: highlightColor,
      note: '',
      rects,
      createdAt: new Date().toISOString(),
    };
    updateHighlights([...highlights, hl]);
    sel.removeAllRanges();
  }, [highlightMode, fileId, highlightColor, highlights, updateHighlights]);

  useEffect(() => {
    if (!highlightMode || !pageContainerRef.current) return;
    const el = pageContainerRef.current;
    const handler = () => handleTextSelection();
    el.addEventListener('mouseup', handler);
    return () => el.removeEventListener('mouseup', handler);
  }, [highlightMode, handleTextSelection]);

  const updateHighlightNote = (id: string, note: string) => updateHighlights(highlights.map(h => h.id === id ? { ...h, note } : h));
  const deleteHighlight = (id: string) => updateHighlights(highlights.filter(h => h.id !== id));

  // Measure container width for fit-to-width
  const [containerWidth, setContainerWidth] = useState(0);
  const [zoomDelta, setZoomDelta] = useState(0); // 0 = fit width, +/- adjusts

  useEffect(() => {
    if (!pdfAreaRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(pdfAreaRef.current);
    return () => observer.disconnect();
  }, [loading]);

  // Page width = container width minus padding, adjusted by zoom
  const basePageWidth = Math.max(containerWidth - 20, 200);
  const pageWidth = Math.round(basePageWidth * Math.pow(1.25, zoomDelta));

  const isPDF = fileInfo?.mimeType === 'application/pdf' || fileInfo?.fileName?.endsWith('.pdf');
  const isImage = fileInfo?.mimeType?.startsWith('image/');
  const title = fileInfo?.fileName || 'File Viewer';
  useEffect(() => { document.title = title; }, [title]);

  // Toolbar button style
  const btnCls = `p-1.5 rounded transition-colors`;
  const btnStyle = { color: S.textMuted };
  const btnHover = { ':hover': { backgroundColor: S.bgHover } };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ background: S.bg }}>
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent" style={{ borderColor: S.textFaint, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div className="h-full flex items-center justify-center" style={{ background: S.bg }}>
        <p style={{ color: S.textMuted }} className="text-sm">{error || 'File not found'}</p>
      </div>
    );
  }

  // PDF Viewer
  if (isPDF) {
    return (
      <div className="h-full flex flex-col" style={{ background: S.bg }}>
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 flex-shrink-0" style={{ background: S.bgPanel, borderBottom: `1px solid ${S.border}` }}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium truncate max-w-xs" style={{ color: S.text }}>{title}</span>
            <button onClick={copyViewerLink} className={`${btnCls} hover:bg-[#27272a]`} style={{ color: linkCopied ? '#22c55e' : S.textMuted }} title={linkCopied ? 'Copied!' : 'Copy link'}>
              {linkCopied
                ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
              }
            </button>
          </div>
          {highlightMode && (
            <span className="px-2 py-0.5 text-xs rounded-full" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }}>Select text to highlight</span>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: S.textMuted }}>{numPages > 0 ? `${numPages} pages` : ''}</span>
            <span className="mx-1 h-4 w-px" style={{ background: S.border }} />
            <button
              onClick={() => setHighlightMode(!highlightMode)}
              className={`${btnCls} flex items-center gap-1 text-xs hover:bg-[#27272a]`}
              style={{ color: highlightMode ? '#fbbf24' : S.textMuted, background: highlightMode ? 'rgba(251,191,36,0.15)' : undefined }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
              Highlight
            </button>
            {highlightMode && HIGHLIGHT_COLORS.map(({ name, color }) => (
              <button key={color} onClick={() => setHighlightColor(color)} className="w-4 h-4 rounded-full" style={{ backgroundColor: color, border: highlightColor === color ? '2px solid white' : '2px solid transparent', transform: highlightColor === color ? 'scale(1.25)' : undefined }} title={name} />
            ))}
            <span className="mx-1 h-4 w-px" style={{ background: S.border }} />
            <button onClick={() => setZoomDelta(d => d - 1)} className={`${btnCls} hover:bg-[#27272a]`} style={btnStyle}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
            </button>
            <button
              onClick={() => setZoomDelta(0)}
              className="text-xs min-w-[3rem] text-center rounded px-1 py-0.5 hover:bg-[#27272a] transition-colors"
              style={{ color: zoomDelta !== 0 ? '#60a5fa' : S.textMuted }}
              title="Reset to fit width"
            >
              {zoomDelta !== 0 ? `${Math.round((pageWidth / basePageWidth) * 100)}%` : 'Fit'}
            </button>
            <button onClick={() => setZoomDelta(d => d + 1)} className={`${btnCls} hover:bg-[#27272a]`} style={btnStyle}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            </button>
            <span className="mx-1 h-4 w-px" style={{ background: S.border }} />
            <button onClick={downloadFile} className={`${btnCls} hover:bg-[#27272a]`} style={btnStyle} title="Download">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            </button>
            <button onClick={() => setShowPanel(!showPanel)} className={`${btnCls} hover:bg-[#27272a]`} style={{ color: showPanel ? '#60a5fa' : S.textMuted }} title={showPanel ? 'Hide panel' : 'Show panel'}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
            </button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* PDF */}
          <div ref={pdfAreaRef} className="flex-1 min-h-0 overflow-auto" style={{ background: '#1a1a1e' }}>
            <div ref={pageContainerRef} className={`flex flex-col items-center gap-2 p-2 ${highlightMode ? 'cursor-text select-text' : ''}`} style={{ userSelect: highlightMode ? 'text' : 'none' }}>
              <Document
                file={blobUrl}
                onLoadSuccess={({ numPages: n }) => { setNumPages(n); }}
                loading={<div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent" style={{ borderColor: S.textFaint, borderTopColor: 'transparent' }} />}
                error={<p style={{ color: '#ef4444' }}>Failed to render PDF</p>}
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <div key={i + 1} className="relative">
                    <Page
                      pageNumber={i + 1}
                      width={pageWidth}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                    />
                    {/* Highlight overlays for this page (only ratio-based rects where all values are 0-1) */}
                    {highlights
                      .filter(hl => hl.pageNumber === i + 1 && hl.rects?.length > 0 && hl.rects.every(r => r.x <= 1 && r.y <= 1 && r.width <= 1 && r.height <= 1))
                      .map(hl => (
                        <div key={hl.id} className="pointer-events-none absolute inset-0">
                          {hl.rects.map((rect, ri) => (
                            <div
                              key={ri}
                              className="absolute mix-blend-multiply"
                              style={{
                                left: `${rect.x * 100}%`,
                                top: `${rect.y * 100}%`,
                                width: `${rect.width * 100}%`,
                                height: `${rect.height * 100}%`,
                                backgroundColor: hl.color,
                                opacity: 0.4,
                              }}
                            />
                          ))}
                        </div>
                      ))
                    }
                  </div>
                ))}
              </Document>
            </div>
          </div>

          {/* Annotations panel */}
          {showPanel && <div className="w-80 flex flex-col flex-shrink-0 overflow-hidden" style={{ background: S.bgPanel, borderLeft: `1px solid ${S.border}` }}>
            <div className="px-4 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${S.border}` }}>
              <h3 className="text-xs font-medium uppercase tracking-wide" style={{ color: S.textFaint }}>
                {highlights.length > 0 ? `${highlights.length} Annotation${highlights.length !== 1 ? 's' : ''}` : 'Annotations'}
              </h3>
            </div>
            <div className="flex-1 overflow-auto p-3 space-y-2">
              {highlights.length === 0 ? (
                <p className="text-xs text-center py-8" style={{ color: S.textFaint }}>
                  Enable highlight mode and select text to annotate
                </p>
              ) : (
                highlights.map(hl => (
                  <div
                    key={hl.id}
                    className="rounded-lg cursor-pointer transition-colors"
                    style={{
                      background: hl.pageNumber === currentPage ? S.bgHover : S.bgCard,
                      border: `1px solid ${hl.pageNumber === currentPage ? S.textFaint : S.border}`,
                    }}
                    onClick={() => {
                      const pageEl = pageContainerRef.current?.querySelector(`[data-page-number="${hl.pageNumber}"]`);
                      pageEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                  >
                    <div className="px-3 py-2 flex items-start gap-2">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: hl.color }} />
                      <p className="flex-1 text-xs line-clamp-2" style={{ color: S.text }}>&ldquo;{hl.text}&rdquo;</p>
                      <button onClick={(e) => { e.stopPropagation(); deleteHighlight(hl.id); }} className="flex-shrink-0 hover:opacity-80" style={{ color: S.textFaint }}>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                    <div className="px-3 pb-2">
                      <textarea
                        value={hl.note}
                        onChange={(e) => updateHighlightNote(hl.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Add note..."
                        rows={hl.note ? 2 : 1}
                        className="w-full text-xs rounded px-2 py-1 resize-none focus:outline-none"
                        style={{ color: S.text, background: '#27272a', border: `1px solid ${S.border}`, }}
                      />
                    </div>
                    <div className="px-3 pb-2">
                      <span className="text-[10px]" style={{ color: S.textFaint }}>p.{hl.pageNumber}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>}
        </div>
      </div>
    );
  }

  // Image Viewer
  if (isImage) {
    return (
      <div className="h-full flex flex-col" style={{ background: S.bg }}>
        <div className="flex items-center justify-between px-4 py-2 flex-shrink-0" style={{ background: S.bgPanel, borderBottom: `1px solid ${S.border}` }}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium truncate" style={{ color: S.text }}>{title}</span>
            <button onClick={copyViewerLink} className={`${btnCls} hover:bg-[#27272a]`} style={{ color: linkCopied ? '#22c55e' : S.textMuted }} title={linkCopied ? 'Copied!' : 'Copy link'}>
              {linkCopied
                ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
              }
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadFile} className={`${btnCls} hover:bg-[#27272a]`} style={btnStyle} title="Download">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            </button>
            <button onClick={() => setShowPanel(!showPanel)} className={`${btnCls} hover:bg-[#27272a]`} style={{ color: showPanel ? '#60a5fa' : S.textMuted }} title={showPanel ? 'Hide notes' : 'Show notes'}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
            </button>
          </div>
        </div>
        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 overflow-auto flex items-center justify-center p-2" style={{ background: '#1a1a1e' }}>
            <img src={blobUrl} alt={title} className="max-w-full max-h-full object-contain" />
          </div>
          {showPanel && <div className="w-80 flex flex-col flex-shrink-0" style={{ background: S.bgPanel, borderLeft: `1px solid ${S.border}` }}>
            <div className="px-4 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${S.border}` }}>
              <h3 className="text-xs font-medium uppercase tracking-wide" style={{ color: S.textFaint }}>Notes</h3>
            </div>
            <div className="flex-1 px-4 py-3 overflow-auto">
              <textarea
                value={imageNotes}
                onChange={(e) => handleImageNotesChange(e.target.value)}
                placeholder="Add notes about this image..."
                className="w-full h-full text-sm bg-transparent border-none outline-none resize-none leading-relaxed"
                style={{ color: S.text }}
              />
            </div>
          </div>}
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="h-full flex items-center justify-center" style={{ background: S.bg }}>
      <div className="text-center">
        <p className="font-medium mb-2" style={{ color: S.text }}>{title}</p>
        <a href={blobUrl} download={fileInfo?.fileName} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm">Download</a>
      </div>
    </div>
  );
};

export default FileViewer;
